import Head from "next/head";
import { useEffect, useMemo, useState } from "react";
import CategoryList from "@/components/categories/CategoryList";
import { useRouter } from "next/router";
import ProductList from "@/components/products/ProductList";
import { fetchMenu } from "@/services/menuService";
import {
  capitalizeText,
  getMetaData,
  getImageUrl,
  parseLinkParams,
  processCategories,
  processCategoryAndProducts,
} from "@/utilities/menu";
import Footer from "@/components/layout/Footer";
import LanguagePopup from "@/components/LanguagePopup";
import MenuButton from "@/components/MenuButton";
import { Category, Menu } from "@/types/types";
import AdSenseAd from "@/components/google-adSense/AdSenseAd";
import MenuInfoSection from "@/components/MenuInfoSection";
import * as gtag from "@/lib/gtag";
import AdsComponent from "@/components/ads-component/AdsComponent";
import Cart from "@/components/cart/Cart";
import RestaurantSchema from "@/components/RestaurantSchema";
import { useTranslation } from "@/hooks/useTranslation";
export const getServerSideProps = async (
  context: any
): Promise<
  { props: HomeProps } | { notFound: boolean } | { redirect: any }
> => {
  const { params } = context.query;
  if (!params || params.length > 3) {
    return { notFound: true };
  }

  const { siteName, categoryName, language, redirectNotFound } =
    parseLinkParams(params);
  if (redirectNotFound) {
    return { notFound: true };
  }

  let menu: Menu | undefined;
  let pageType = "HOME";

  if (siteName) {
    const data = await fetchMenu(siteName);
    if (data) {
      menu = data as Menu;
    } else {
      return { notFound: true };
    }
  } else {
    return { notFound: true };
  }

  if (language && !menu?.languages?.includes(language)) {
    return { notFound: true };
  }

  const normalizedLanguage = (language || menu?.languages?.[0] || "en").split(
    "_"
  )[0];

  const isDefaultLanguage =
    !menu?.languages ||
    menu?.languages?.[0]?.includes(normalizedLanguage) ||
    menu?.languages?.length === 0;

  if (categoryName === "cart" || categoryName === "thank-you") {
    return {
      redirect: {
        destination: `/menus/checkout/?menu=${siteName}`,
        permanent: true,
      },
    };
  }

  if (categoryName && categoryName !== "null") {
    pageType = "CATEGORY";

    const category = menu.categories?.find(
      (category: Category) => category.pageName === categoryName?.trim()
    );
    if (category) {
      processCategoryAndProducts(
        menu,
        category,
        normalizedLanguage,
        isDefaultLanguage
      );
    } else {
      return {
        redirect: {
          destination: `/menus/${encodeURIComponent(siteName)}/`,
          permanent: true,
        },
      };
    }
    menu.categories = [];
  } else {
    menu.products = [];
    menu.categories = processCategories(
      menu.categories,
      menu.categoriesOrder,
      menu?.companyInfo?.timezone,
      normalizedLanguage,
      isDefaultLanguage
    ) as any;
  }

  const menuName = capitalizeText(siteName.replace(/-/g, " "));
  const city = getCity(menu);

  const { seoTitle, seoDescription } = getMetaData(
    menu?.companyInfo?.storeIndustry || "restaurant",
    city,
    menuName,
    pageType,
    menu?.categoryTitle || "",
    normalizedLanguage
  );
  // Here we are passing user display title
  menu.displayName = menu?.title;

  // Here we are forcing title and description that match seo requirements
  menu.title = seoTitle;
  menu.description = seoDescription;

  menu.categoriesOrder = [];

  menu.logo = getImageUrl(menu?.logo || "");

  return {
    props: {
      menu,
      language: normalizedLanguage,
      isDefaultLanguage: !!isDefaultLanguage,
    },
  };
};

const getCity = (menu: Menu): string => {
  return (
    menu?.companyInfo?.city ||
    menu?.companyInfo?.country ||
    menu?.companyInfo?.address ||
    "Your City"
  );
};

interface HomeProps {
  menu: Menu;
  language: string;
  isDefaultLanguage: boolean;
}

const MenuPage: React.FC<HomeProps> = ({
  menu,
  language,
  isDefaultLanguage,
}) => {
  const { translate } = useTranslation(
    ["ar", "fr"].includes(language) ? language : "en"
  );

  const [showCategories, setShowCategories] = useState(true);
  // const [categories, setCategories] = useState<any>();
  // const [catLoading, setCatLoading] = useState<boolean>(true);
  const [isLanguagePopupOpen, setIsLanguagePopupOpen] = useState(false);
  const router = useRouter();
  const { menu: displayCategories } = router.query;

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);
  useEffect(() => {
    const { siteName, categoryName } = parseLinkParams(
      router.query.params as string[]
    );
    if (!categoryName && !showCategories) {
      gtag.event(
        "action",
        {
          event_label: "Home page Event",
          event_category: "engagement",
        },
        siteName!
      );
    }
    if (!categoryName && showCategories) {
      gtag.event(
        "action",
        {
          event_label: "Categories list Event",
          event_category: "engagement",
        },
        siteName!
      );
    }
    if (!!categoryName) {
      gtag.event(
        "action",
        {
          event_label: `Show Category Event`,
          value: categoryName,
          event_category: "engagement",
        },
        siteName!
      );
    }
  }, [router.query, showCategories]);
  useEffect(() => {
    setShowCategories(displayCategories === "1");
  }, [displayCategories]);

  // useEffect(() => {
  //   fetchCategories(menu).then((categories) => {
  //     if (categories) {
  //       if (!isDefaultLanguage) {
  //         categories = categories.map((category: any) => {
  //           return {
  //             ...category,
  //             title:
  //               category.titles && category.titles[language]
  //                 ? category.titles[language]
  //                 : category.title,
  //           };
  //         });
  //       }
  //       setCategories(categories);
  //       setCatLoading(false);
  //     }
  //   });
  // }, [isDefaultLanguage, language, menu]);

  const toggleCategories = () => setShowCategories((prev) => !prev);
  const toggleLanguages = () => setIsLanguagePopupOpen((prev) => !prev);

  const menuName = useMemo(
    () =>
      capitalizeText(menu?.displayName || menu?.siteName?.replace(/-/g, " ")),
    [menu]
  );
  const menuImage = useMemo(() => getImageUrl(menu?.imageMenu || ""), [menu]);

  const MenuButtons = () => {
    return (
      <div className="grid grid-cols-2 px-4 pt-[49px] justify-between">
        <MenuButton
          label={translate("our_menu")}
          href={"?menu=1"}
          onClick={toggleCategories}
          icon={
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="49"
              height="49"
              viewBox="0 0 49 49"
              fill="none"
            >
              <g clipPath="url(#clip0_138_728)">
                <path
                  d="M23.4159 48.5529C23.5581 48.6955 23.7271 48.8086 23.9131 48.8858C24.0992 48.963 24.2986 49.0027 24.5 49.0027C24.7014 49.0027 24.9008 48.963 25.0869 48.8858C25.2729 48.8086 25.4419 48.6955 25.5841 48.5529L31.2589 42.875H42.875C44.4995 42.875 46.0574 42.2297 47.206 41.081C48.3547 39.9324 49 38.3745 49 36.75V9.1875C49 7.56305 48.3547 6.00513 47.206 4.85647C46.0574 3.70781 44.4995 3.0625 42.875 3.0625H6.125C4.50055 3.0625 2.94263 3.70781 1.79397 4.85647C0.645311 6.00513 0 7.56305 0 9.1875L0 36.75C0 38.3745 0.645311 39.9324 1.79397 41.081C2.94263 42.2297 4.50055 42.875 6.125 42.875H17.7411L23.4159 48.5529ZM3.0625 27.5625V18.375H45.9375V27.5625H3.0625ZM45.9375 30.625V36.75C45.9375 37.5622 45.6148 38.3412 45.0405 38.9155C44.4662 39.4898 43.6872 39.8125 42.875 39.8125H31.2589C30.4468 39.8127 29.6679 40.1354 29.0938 40.7098L24.5 45.3036L19.9062 40.7098C19.3321 40.1354 18.5532 39.8127 17.7411 39.8125H6.125C5.31277 39.8125 4.53382 39.4898 3.95949 38.9155C3.38516 38.3412 3.0625 37.5622 3.0625 36.75V30.625H45.9375ZM45.9375 15.3125H3.0625V9.1875C3.0625 8.37527 3.38516 7.59632 3.95949 7.02199C4.53382 6.44766 5.31277 6.125 6.125 6.125H42.875C43.6872 6.125 44.4662 6.44766 45.0405 7.02199C45.6148 7.59632 45.9375 8.37527 45.9375 9.1875V15.3125ZM6.125 35.2188C6.125 35.6249 6.28633 36.0143 6.57349 36.3015C6.86066 36.5887 7.25014 36.75 7.65625 36.75H32.1562C32.5624 36.75 32.9518 36.5887 33.239 36.3015C33.5262 36.0143 33.6875 35.6249 33.6875 35.2188C33.6875 34.8126 33.5262 34.4232 33.239 34.136C32.9518 33.8488 32.5624 33.6875 32.1562 33.6875H7.65625C7.25014 33.6875 6.86066 33.8488 6.57349 34.136C6.28633 34.4232 6.125 34.8126 6.125 35.2188ZM6.125 22.9688C6.125 23.3749 6.28633 23.7643 6.57349 24.0515C6.86066 24.3387 7.25014 24.5 7.65625 24.5H41.3438C41.7499 24.5 42.1393 24.3387 42.4265 24.0515C42.7137 23.7643 42.875 23.3749 42.875 22.9688C42.875 22.5626 42.7137 22.1732 42.4265 21.886C42.1393 21.5988 41.7499 21.4375 41.3438 21.4375H7.65625C7.25014 21.4375 6.86066 21.5988 6.57349 21.886C6.28633 22.1732 6.125 22.5626 6.125 22.9688ZM6.125 10.7188C6.125 11.1249 6.28633 11.5143 6.57349 11.8015C6.86066 12.0887 7.25014 12.25 7.65625 12.25H26.0312C26.4374 12.25 26.8268 12.0887 27.114 11.8015C27.4012 11.5143 27.5625 11.1249 27.5625 10.7188C27.5625 10.3126 27.4012 9.92316 27.114 9.63599C26.8268 9.34883 26.4374 9.1875 26.0312 9.1875H7.65625C7.25014 9.1875 6.86066 9.34883 6.57349 9.63599C6.28633 9.92316 6.125 10.3126 6.125 10.7188Z"
                  fill="#212121"
                />
              </g>
              <defs>
                <clipPath id="clip0_138_728">
                  <rect width="49" height="49" fill="white" />
                </clipPath>
              </defs>
            </svg>
          }
        />
        <MenuButton
          label={translate("reviews")}
          href={`/menus/reviews?menu=${menu.siteName}`}
          icon={
            <svg
              width="55"
              height="54"
              viewBox="0 0 55 54"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M36.7784 17.7756L28.0958 0L19.4132 17.7756L0 20.6261L14.0479 34.4636L10.7306 54L24.2784 46.8045V42.4459L15.802 46.9491L17.8099 35.1154L18.1497 33.1155L16.7124 31.7019L8.20352 23.3184L19.9629 21.5923L21.9479 21.3011L22.8374 19.4824L28.0958 8.71521L33.3542 19.4824L34.2437 21.3011L36.2287 21.5923L50.456 23.6848L51 19.8643L36.7784 17.7756Z"
                fill="black"
              />
              <path
                d="M32 46.2H47.3333V50H32V46.2ZM32 38.6H55V42.4H32V38.6ZM32 31H55V34.8H32V31Z"
                fill="black"
              />
            </svg>
          }
        />
        {menu.orderOnline && (
          <MenuButton
            label={translate("order_online")}
            subLabel={translate("(soon)")}
            disabled={true}
            href=""
            icon={
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="52"
                height="52"
                viewBox="0 0 52 52"
                fill="none"
              >
                <path
                  d="M45.5 13V45.5C45.5 46.3971 45.3307 47.235 44.9922 48.0137C44.6536 48.7923 44.1882 49.4779 43.5957 50.0703C43.0033 50.6628 42.3092 51.1283 41.5137 51.4668C40.7181 51.8053 39.8802 51.9831 39 52H9.75C8.85286 52 8.01497 51.8307 7.23633 51.4922C6.45768 51.1536 5.77214 50.6882 5.17969 50.0957C4.58724 49.5033 4.12174 48.8177 3.7832 48.0391C3.44466 47.2604 3.26693 46.4141 3.25 45.5V13H9.75V9.75C9.75 8.41276 10.0039 7.15169 10.5117 5.9668C11.0195 4.7819 11.722 3.74935 12.6191 2.86914C13.5163 1.98893 14.5488 1.29492 15.7168 0.787109C16.8848 0.279297 18.1458 0.0169271 19.5 0C21.2604 0 22.8939 0.440104 24.4004 1.32031C25.9069 0.440104 27.5234 0 29.25 0C30.5872 0 31.8483 0.253906 33.0332 0.761719C34.2181 1.26953 35.2507 1.97201 36.1309 2.86914C37.0111 3.76628 37.7051 4.79883 38.2129 5.9668C38.7207 7.13477 38.9831 8.39583 39 9.75V13H45.5ZM35.75 9.75C35.75 8.86979 35.5807 8.0319 35.2422 7.23633C34.9036 6.44076 34.4382 5.75521 33.8457 5.17969C33.2533 4.60417 32.5592 4.13867 31.7637 3.7832C30.9681 3.42773 30.1302 3.25 29.25 3.25C28.4883 3.25 27.7689 3.37695 27.0918 3.63086C27.5827 4.24023 27.972 4.84115 28.2598 5.43359C28.5475 6.02604 28.7591 6.61849 28.8945 7.21094C29.0299 7.80339 29.123 8.4043 29.1738 9.01367C29.2246 9.62305 29.25 10.2747 29.25 10.9688V13H35.75V9.75ZM13 13H26V9.75C26 8.86979 25.8307 8.0319 25.4922 7.23633C25.1536 6.44076 24.6882 5.75521 24.0957 5.17969C23.5033 4.60417 22.8092 4.13867 22.0137 3.7832C21.2181 3.42773 20.3802 3.25 19.5 3.25C18.6029 3.25 17.765 3.41927 16.9863 3.75781C16.2077 4.09635 15.5221 4.56185 14.9297 5.1543C14.3372 5.74674 13.8717 6.44076 13.5332 7.23633C13.1947 8.0319 13.0169 8.86979 13 9.75V13ZM33.3633 48.75C32.7878 47.7344 32.5 46.651 32.5 45.5V16.25H6.5V45.5C6.5 45.957 6.58464 46.3802 6.75391 46.7695C6.92318 47.1589 7.15169 47.4974 7.43945 47.7852C7.72721 48.0729 8.07422 48.3099 8.48047 48.4961C8.88672 48.6823 9.3099 48.7669 9.75 48.75H33.3633ZM42.25 16.25H35.75V45.5C35.75 45.957 35.8346 46.3802 36.0039 46.7695C36.1732 47.1589 36.4017 47.4974 36.6895 47.7852C36.9772 48.0729 37.3242 48.3099 37.7305 48.4961C38.1367 48.6823 38.5599 48.7669 39 48.75C39.457 48.75 39.8802 48.6654 40.2695 48.4961C40.6589 48.3268 40.9974 48.0983 41.2852 47.8105C41.5729 47.5228 41.8099 47.1758 41.9961 46.7695C42.1823 46.3633 42.2669 45.9401 42.25 45.5V16.25Z"
                  fill="black"
                />
              </svg>
            }
          />
        )}
        {menu.orderOnline && (
          <MenuButton
            label={translate("checkout")}
            disabled={true}
            subLabel={translate("apple_pay_(soon)")}
            href=""
            icon={
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="83"
                height="34"
                viewBox="0 0 83 34"
                fill="none"
              >
                <g clipPath="url(#clip0_138_754)">
                  <path
                    d="M15.1684 4.36731C14.1958 5.51138 12.6393 6.41408 11.083 6.285C10.8885 4.73809 11.6504 3.09416 12.5421 2.07883C13.5146 0.902531 15.217 0.064455 16.595 0C16.7571 1.61154 16.1247 3.19084 15.1684 4.36731ZM16.5787 6.59133C14.3253 6.46242 12.3962 7.86431 11.3262 7.86431C10.2401 7.86431 8.60265 6.65578 6.81943 6.68801C4.50129 6.72024 2.34509 8.02545 1.16163 10.1044C-1.27014 14.2623 0.529454 20.4185 2.88001 23.8027C4.03105 25.4787 5.40903 27.3159 7.22467 27.2514C8.94321 27.1869 9.62402 26.1396 11.6992 26.1396C13.7904 26.1396 14.3903 27.2514 16.2059 27.2192C18.0864 27.1869 19.2699 25.5433 20.4209 23.8672C21.7339 21.9656 22.269 20.1122 22.3014 20.0155C22.269 19.9833 18.6701 18.6136 18.6377 14.4879C18.6053 11.0392 21.4747 9.39544 21.6044 9.29876C19.9831 6.9136 17.4542 6.65578 16.5787 6.59133ZM29.5967 1.9177V27.0421H33.5197V18.4525H38.9506C43.9112 18.4525 47.3968 15.0683 47.3968 10.1691C47.3968 5.27 43.9761 1.91786 39.0803 1.91786H29.5965L29.5967 1.9177ZM33.5197 5.20538H38.0427C41.4472 5.20538 43.3925 7.01028 43.3925 10.185C43.3925 13.3597 41.4472 15.1809 38.0265 15.1809H33.5197V5.20538ZM54.5622 27.2353C57.0264 27.2353 59.3121 25.9945 60.3497 24.0283H60.4308V27.0419H64.0622V14.5362C64.0622 10.9103 61.1441 8.57348 56.6534 8.57348C52.4871 8.57348 49.4069 10.9425 49.2935 14.1978H52.8276C53.1194 12.6507 54.5621 11.6354 56.54 11.6354C58.9393 11.6354 60.2849 12.7474 60.2849 14.7942V16.18L55.3889 16.47C50.8335 16.744 48.3695 18.5974 48.3695 21.8206C48.3695 25.0759 50.9146 27.2353 54.5622 27.2353ZM55.6159 24.2539C53.5248 24.2539 52.1953 23.2549 52.1953 21.7237C52.1953 20.1446 53.476 19.226 55.924 19.0809L60.2849 18.807V20.225C60.2849 22.5779 58.2747 24.2539 55.616 24.2539H55.6159ZM68.9094 33.875C72.7353 33.875 74.5347 32.4247 76.1072 28.025L82.9971 8.81518H79.0091L74.3889 23.6576H74.3078L69.6876 8.81518H65.5861L72.2327 27.1064L71.876 28.2184C71.2763 30.1039 70.3035 30.8291 68.569 30.8291C68.261 30.8291 67.6612 30.7969 67.4179 30.7647V33.7783C67.6449 33.8427 68.6174 33.875 68.9094 33.875Z"
                    fill="black"
                  />
                </g>
                <defs>
                  <clipPath id="clip0_138_754">
                    <rect width="83" height="34" fill="white" />
                  </clipPath>
                </defs>
              </svg>
            }
          />
        )}
      </div>
    );
  };

  return (
    <>
      <Head>
        <title>{menu.title}</title>
        <meta name="description" content={menu.description} />
        <link rel="icon" href="/favicon.ico" />
        <meta property="og:title" content={menu.title} />
        <meta property="og:description" content={menu.description} />
        {menuImage && <meta property="og:image" content={menuImage} />}
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={menu.title} />
        <meta name="twitter:description" content={menu.description} />
        {menuImage && <meta property="twitter:image" content={menuImage} />}
        <link
          rel="canonical"
          href={`https://qrmenu.com/menus/${menu.siteName}${
            menu?.categoryTitle ? `/${menu?.categoryPageName}` : ""
          }/`}
        />

        {menu.languages?.map((lang, index) => {
          return (
            <link
              key={index}
              rel="alternate"
              hrefLang={lang}
              href={`https://qrmenu.com/menus/${menu.siteName}${
                menu?.categoryTitle ? `/${menu?.categoryPageName}` : ""
              }/${lang}/`}
            />
          );
        })}
      </Head>
      <div>
        <Cart
          siteName={menu.siteName}
          currency={menu.currency}
          language={language}
        />
        {/* <MenuBackground menuImage={menuImage} /> */}
        {/* <div
          className="h-56 sm:h-64 bg-center bg-cover bg-no-repeat "
          style={{
            backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.3)), url('${menuImage}') `,
          }}
        ></div> */}
        <div
          className="relative sm:h-56 h-[200px] w-full overflow-hidden"
          style={{ background: `url(${menuImage})` }}
        >
          <img
            src={menuImage}
            alt={`Menu image of ${menu.title}`}
            className="object-cover object-center w-full absolute md:top-[-40%]"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black opacity-30"></div>
        </div>

        <h1 className="mx-[6%] relative whitespace-nowrap overflow-hidden">
          {menu.title}
        </h1>
        <div className="flex flex-col min-h-screen bg-white font-inter rounded-tr-[70px] overflow-hidden mt-[-80px] relative">
          <div className="mx-[5%] bg-white flex justify-between items-center mt-[22px]">
            <a
              href={`/menus/${menu.siteName}/${
                !isDefaultLanguage ? language : ""
              }${menu?.categoryTitle ? "?menu=1" : ""}`}
            >
              <div className="flex items-center">
                {menu?.categoryTitle || showCategories ? (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="36"
                    height="36"
                    viewBox="7 0 36 36"
                    fill="none"
                    style={{ minWidth: "36px" }}
                  >
                    <path
                      d="M15.0823 10.4175L15.0823 16.4175L28.4623 16.4175L28.5073 19.4325L15.0823 19.4325L15.0823 25.4175L7.58232 17.9175L15.0823 10.4175Z"
                      fill="black"
                    />
                  </svg>
                ) : (
                  <></>
                )}
                <div className="flex ml-1 items-center gap-2">
                  {menu.logo?.length &&
                  !menu?.categoryTitle &&
                  !showCategories ? (
                    <img
                      src={`${getImageUrl(menu.logo)}`}
                      alt={menu.title || ""}
                      width="100px"
                      height="70px"
                      className="rounded-md"
                      style={{
                        width: "auto",
                        objectFit: "contain",
                        objectPosition: "left",
                        maxWidth: "70px",
                        height: "70px",
                        borderRadius: "50%",
                        padding: "3px",
                        border: "1px solid black",
                      }}
                    />
                  ) : (
                    <></>
                  )}
                  <h2 className="text-[22px] font-bold text-gray-900 leading-[30px] capitalize">
                    {menu?.categoryTitle ? menu?.categoryTitle : menuName}
                  </h2>
                </div>
              </div>
              {!menu?.categoryTitle && (
                <span className="text-[15px] text-gray-900 leading-[10px]">
                  {menu?.tagLine}
                </span>
              )}
            </a>
            {!menu?.categoryTitle && (
              <LanguagePopup
                currentLanguage={language}
                isOpen={isLanguagePopupOpen}
                togglePopup={toggleLanguages}
                languages={menu?.languages}
                siteName={menu?.siteName}
              />
            )}
          </div>

          {!!menu?.categoryTitle ? (
            <ProductList
              products={menu.products}
              language={language}
              country={menu.companyInfo?.country || ""}
              subscribed={menu.subscribed}
            />
          ) : (
            <>
              <div className={showCategories ? "block" : "hidden"}>
                <CategoryList
                  categories={menu.categories || []}
                  siteName={menu.siteName}
                  language={!isDefaultLanguage ? language : ""}
                  subscribed={menu.subscribed}
                  showCategories={showCategories}
                />
              </div>
              <div className={showCategories ? "hidden" : "block"}>
                <MenuButtons />
              </div>
            </>
          )}
          {/* Aff ads */}
          {!menu.subscribed ? (
            <AdsComponent
              menuName={menu.siteName}
              country={menu.companyInfo?.country}
            />
          ) : (
            <></>
          )}

          {/* Google ads */}
          {!menu.subscribed ? (
            <div className="mt-5 mb-[5%] mx-[5%] text-right">
              <AdSenseAd
                adClient="ca-pub-9101961507466056"
                adSlot="9338909219"
                style={{
                  height: "300px",
                }} // Square dimensions
                format="auto"
                fullWidthResponsive="true"
              />
            </div>
          ) : (
            <></>
          )}

          {/* Bottom Restaurant Info Section */}
          <MenuInfoSection
            companyInfo={menu?.companyInfo}
            menuTitle={menuName}
            tagLine={menu?.tagLine}
          />

          {/* Footer Section */}
          <Footer siteName={menu.siteName} loadAds={!menu.subscribed} />
          <RestaurantSchema menuData={menu} />
        </div>
      </div>
    </>
  );
};
export default MenuPage;
